import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react'
import { IconCheck, IconAlertTriangle, IconInfoCircle, IconX } from '@tabler/icons-react'

type ToastType = 'success' | 'error' | 'info' | 'warning'
type Toast = { id: string; type: ToastType; title: string; message?: string; duration?: number }
type ToastContextValue = {
  toast: (opts: Omit<Toast, 'id'>) => void
  success: (title: string, message?: string) => void
  error: (title: string, message?: string) => void
  info: (title: string, message?: string) => void
  warning: (title: string, message?: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const toast = useCallback((opts: Omit<Toast, 'id'>) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const duration = opts.duration ?? (opts.type === 'error' ? 6000 : 4000)
    setToasts(prev => [...prev, { ...opts, id, duration }])
    if (duration > 0) setTimeout(() => dismiss(id), duration)
  }, [dismiss])

  const success = useCallback((title: string, message?: string) => toast({ type: 'success', title, message }), [toast])
  const error = useCallback((title: string, message?: string) => toast({ type: 'error', title, message, duration: 0 }), [toast])
  const info = useCallback((title: string, message?: string) => toast({ type: 'info', title, message }), [toast])
  const warning = useCallback((title: string, message?: string) => toast({ type: 'warning', title, message }), [toast])

  return (
    <ToastContext.Provider value={{ toast, success, error, info, warning }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[600] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map(t => <ToastItem key={t.id} toast={t} onDismiss={dismiss} />)}
      </div>
    </ToastContext.Provider>
  )
}

const ICONS: Record<ToastType, ReactNode> = {
  success: <IconCheck size={14} className="text-emerald-400" />,
  error: <IconAlertTriangle size={14} className="text-red-400" />,
  warning: <IconAlertTriangle size={14} className="text-amber-400" />,
  info: <IconInfoCircle size={14} className="text-blue-400" />,
}

const CLASSES: Record<ToastType, string> = {
  success: 'border-emerald-900/50 bg-emerald-950/80',
  error: 'border-red-900/50 bg-red-950/80',
  warning: 'border-amber-900/50 bg-amber-950/80',
  info: 'border-blue-900/50 bg-blue-950/80',
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => { requestAnimationFrame(() => setVisible(true)) }, [])
  return (
    <div className={[
      'flex items-start gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm pointer-events-auto',
      'transition-all duration-200 shadow-lg',
      CLASSES[toast.type],
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
    ].join(' ')}>
      <span className="shrink-0 mt-0.5">{ICONS[toast.type]}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-zinc-200">{toast.title}</div>
        {toast.message && <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{toast.message}</div>}
      </div>
      <button onClick={() => onDismiss(toast.id)} className="shrink-0 p-0.5 text-zinc-600 hover:text-zinc-400 transition-colors">
        <IconX size={13} />
      </button>
    </div>
  )
}
